import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "楽天RMS「画像サイズが規定外です」アップロードできない時の対処法",
	description:
		"楽天RMSで「画像サイズが規定外です」が出る原因（解像度・ファイルサイズ・形式）と、R-Cabinetにアップロードできるようにする具体的な手順を解説。",
	publishDate: "2026-03-16",
	slug: "rakuten-rms-image-size-error",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function RakutenRmsImageSizeError() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section className="mb-8">
						<p className="text-gray-700 leading-relaxed">
							楽天市場の管理画面（RMS）でR-Cabinetに画像をアップロードしようとしたら「画像サイズが規定外です」と表示されてしまった——そんな経験はありませんか？
							楽天市場の画像規格はAmazonとは異なる基準があるため、他のモールで使えていた画像でもエラーになることがあります。
							この記事では、エラーの原因と解決手順を解説します。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							エラーの原因：楽天RMSの画像規格
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							楽天市場のR-Cabinetには以下の画像規格があります。この規格を超えた、または下回った場合にエラーが発生します。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4">
							<table className="w-full text-sm text-gray-700">
								<thead>
									<tr className="border-b">
										<th className="text-left py-2 font-semibold">項目</th>
										<th className="text-left py-2 font-semibold">規格</th>
									</tr>
								</thead>
								<tbody>
									<tr className="border-b">
										<td className="py-2">最大解像度</td>
										<td className="py-2">横3,840px × 縦3,840px以下</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">推奨サイズ</td>
										<td className="py-2">700px × 700px以上（実運用推奨）</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">ファイルサイズ</td>
										<td className="py-2">2MB以下（1枚あたり）</td>
									</tr>
									<tr>
										<td className="py-2">対応形式</td>
										<td className="py-2">JPG、JPEG、BMP、GIF、TIF、PNG</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className="text-gray-700 leading-relaxed">
							「画像サイズが規定外です」の原因で最も多いのは次の2パターンです。
						</p>
						<ul className="list-disc list-inside mt-3 space-y-2 text-gray-700 text-sm">
							<li>
								<strong>解像度が3,840pxを超えている</strong>
								：高解像度カメラで撮影した画像はそのまま使うと上限を超えることがあります
							</li>
							<li>
								<strong>ファイルサイズが2MBを超えている</strong>
								：JPEGでも高品質設定だと2MBを超えることがあります
							</li>
						</ul>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">解決手順</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>解像度を3,840px以下にリサイズする</strong>
								<p className="mt-1 ml-5 text-sm">
									Windowsの場合：ペイントで開き「サイズ変更」→ピクセル単位で3,840px以下に変更して保存。
									Macの場合：プレビューで開き「ツール → サイズを調整」で変更。
									推奨サイズは楽天のSEO的にも有利な1,000px × 1,000px前後です。
								</p>
							</li>
							<li>
								<strong>ファイルサイズを2MB以下に圧縮する</strong>
								<p className="mt-1 ml-5 text-sm">
									Squoosh（ブラウザツール）やiloveimg.comを使ってJPEG品質を80〜85%に下げることで
									ほとんどの場合2MB以下に収まります。
									画質の劣化はほぼわかりません。
								</p>
							</li>
							<li>
								<strong>形式をJPGまたはPNGに変換する</strong>
								<p className="mt-1 ml-5 text-sm">
									WebPなど対応外の形式で保存している場合は変換が必要です。
									対応形式はJPG・JPEG・BMP・GIF・TIF・PNGです。
								</p>
							</li>
							<li>
								<strong>RMSのR-Cabinetに再アップロード</strong>
								<p className="mt-1 ml-5 text-sm">
									「店舗構築 → 画像・動画 →
									R-Cabinet」から再アップロードします。
								</p>
							</li>
						</ol>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							しろぬきやで自動変換する方法
						</h2>
						<p className="text-gray-700 leading-relaxed">
							リサイズと圧縮を毎回手作業でやるのは面倒ですよね。
							<strong>しろぬきや</strong>
							ではAI背景除去に加えて、楽天市場の規格（1,000×1,000px・2MB以下）への自動変換も対応しています。
							ドラッグ＆ドロップだけで規格内の画像が完成します。
						</p>
					</section>

					<CtaBlock />
					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
